import React from "react";
import {
  Plus, Pencil, Trash2, Send, Archive, RotateCcw, Search, ChevronLeft, ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { StatusBadge } from "./StatusBadge";
import { BulkActions } from "./BulkActions";

// Node list for the active entity type: search, bulk toolbar, table, pagination.
export const NodeEditor = ({
  nodeType, nodes, nodeTotal, pageSize, nodeQuery, nodeSkip,
  onQueryChange, onPrevPage, onNextPage,
  selected, onToggleSelect, onToggleSelectAll, onClearSelection,
  onRunBulk, onBulkDelete, onNewNode, onEditNode, onManageFields, onNodeAction, onDeleteNode,
  isAdmin,
}) => (
  <div>
    <div className="flex items-center justify-between mb-4">
      <div>
        <h2 className="font-serif text-2xl text-foreground">{nodeType.label}</h2>
        <p className="text-xs text-muted-foreground">{nodeType.layer} · {nodeTotal} entit{nodeTotal === 1 ? "y" : "ies"}</p>
      </div>
      <div className="flex items-center gap-2">
        <div className="relative">
          <Search className="w-4 h-4 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input
            value={nodeQuery}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder={`Search ${nodeType.label.toLowerCase()}…`}
            className="pl-8 h-9 w-48"
            data-testid="node-search-input"
          />
        </div>
        <Button variant="outline" onClick={onManageFields} data-testid="manage-fields-btn">
          <Plus className="w-4 h-4 mr-2" /> Fields
        </Button>
        <Button onClick={onNewNode} data-testid="add-node-btn" className="bg-primary hover:bg-primary/90">
          <Plus className="w-4 h-4 mr-2" /> New {nodeType.label}
        </Button>
      </div>
    </div>

    {selected.size > 0 && (
      <BulkActions
        count={selected.size}
        isAdmin={isAdmin}
        onSubmit={() => onRunBulk("submit")}
        onApprove={() => onRunBulk("approve")}
        onArchive={() => onRunBulk("archive")}
        onDelete={onBulkDelete}
        onClear={onClearSelection}
      />
    )}

    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <table className="w-full text-sm" data-testid="node-table">
        <tbody>
          {nodes.length > 0 && (
            <tr className="border-b border-border bg-secondary/20">
              <td className="pl-5 py-2 w-10">
                <Checkbox
                  checked={selected.size === nodes.length && nodes.length > 0}
                  onCheckedChange={onToggleSelectAll}
                  data-testid="bulk-select-all"
                />
              </td>
              <td colSpan={2} className="px-3 py-2 text-xs text-muted-foreground">Select all on this page</td>
            </tr>
          )}
          {nodes.map((n) => (
            <tr key={n.id} className="border-b border-border last:border-0 hover:bg-secondary/30 transition-colors" data-testid={`node-row-${n.id}`}>
              <td className="pl-5 py-3 w-10 align-top">
                <Checkbox
                  checked={selected.has(n.id)}
                  onCheckedChange={() => onToggleSelect(n.id)}
                  data-testid={`select-node-${n.id}`}
                />
              </td>
              <td className="px-3 py-3">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-foreground">{n.label}</span>
                  <StatusBadge node={n} />
                </div>
                <div className="text-xs text-muted-foreground line-clamp-1 max-w-xl">
                  {n.data.core_meaning || n.data.description || n.data.essence || n.data.metaphysical_position || n.data.role || ""}
                </div>
              </td>
              <td className="px-5 py-3">
                <div className="flex justify-end gap-1 flex-wrap">
                  {n.status === "draft" && (
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-primary" title="Submit for review" onClick={() => onNodeAction(n.id, "submit")} data-testid={`submit-node-${n.id}`}>
                      <Send className="w-4 h-4" />
                    </Button>
                  )}
                  {n.status === "archived" ? (
                    <Button variant="ghost" size="icon" className="h-8 w-8" title="Restore" onClick={() => onNodeAction(n.id, "restore")} data-testid={`restore-node-${n.id}`}>
                      <RotateCcw className="w-4 h-4" />
                    </Button>
                  ) : (
                    <Button variant="ghost" size="icon" className="h-8 w-8" title="Archive" onClick={() => onNodeAction(n.id, "archive")} data-testid={`archive-node-${n.id}`}>
                      <Archive className="w-4 h-4" />
                    </Button>
                  )}
                  <Button variant="ghost" size="icon" className="h-8 w-8" title="Edit" onClick={() => onEditNode(n)} data-testid={`edit-node-${n.id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  {isAdmin && (
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" title="Delete permanently" onClick={() => onDeleteNode(n.id)} data-testid={`delete-node-${n.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
          {nodes.length === 0 && <tr><td colSpan={3} className="px-5 py-8 text-center text-muted-foreground">{nodeQuery ? "No entities match your search." : "No entities yet."}</td></tr>}
        </tbody>
      </table>
    </div>

    {nodeTotal > pageSize && (
      <div className="flex items-center justify-between mt-3" data-testid="node-pagination">
        <span className="text-xs text-muted-foreground">
          {nodeSkip + 1}–{Math.min(nodeSkip + pageSize, nodeTotal)} of {nodeTotal}
        </span>
        <div className="flex gap-1">
          <Button variant="outline" size="sm" disabled={nodeSkip === 0} onClick={onPrevPage} data-testid="node-prev-page">
            <ChevronLeft className="w-4 h-4" /> Prev
          </Button>
          <Button variant="outline" size="sm" disabled={nodeSkip + pageSize >= nodeTotal} onClick={onNextPage} data-testid="node-next-page">
            Next <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    )}
  </div>
);
